import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetSubscriptionResult } from '@whinerp/client';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  MetricTile,
  Num,
  Panel,
  RecordHeader,
  SectionLabel,
  Skeleton,
  type IconName,
} from '@whinerp/ui';
import { formatDate, formatDateTime, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { TermPanel } from './term-panel.js';

/**
 * SC-44 Subscription record.
 *
 * The screen exists to make one thing obvious: nothing on it is a field
 * somebody edits. Every change is an entry with the date it takes effect, and
 * the plan history shows the entries rather than the current values - which is
 * why a correction appears beside the figure it corrected instead of quietly
 * replacing it.
 *
 * The header's MRR and status are the fold's answer as at today. The history
 * below is what produced it, in the order it takes effect, so a reader can
 * check the top of the screen against the bottom without leaving it.
 */

type Subscription = GetSubscriptionResult;
type SubscriptionEvent = Subscription['events'][number];
type EventKind = SubscriptionEvent['kind'];

/** Kinds a person records by hand. 'started' and 'corrected' are not among them:
 *  one is written by creation, the other by the correction flow. */
const CHANGE_KINDS: EventKind[] = [
  'upgraded',
  'downgraded',
  'quantity-changed',
  'price-changed',
  'paused',
  'resumed',
  'cancelled',
  'reactivated',
];

/** The kinds that must say why. Mirrors the server rule rather than replacing it. */
const NEEDS_REASON: EventKind[] = ['cancelled', 'paused'];

function SubscriptionRecordScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const params = useParams();
  const subscriptionId = params['id'] ?? '';

  const [change, setChange] = useState<{ kind: EventKind; effectiveOn: string; mrr: string; reason: string } | null>(
    null,
  );
  const [refusal, setRefusal] = useState<string | null>(null);

  const record = useQuery<Subscription, ApiError>({
    queryKey: ['subscription', subscriptionId],
    queryFn: () => api.getSubscription({ path: { subscriptionId } }),
  });

  const recordChange = useMutation({
    mutationFn: (input: { kind: EventKind; effectiveOn: string; mrr: string; reason: string }) =>
      api.recordSubscriptionEvent({
        path: { subscriptionId },
        body: {
          kind: input.kind,
          effectiveOn: input.effectiveOn,
          mrr: input.mrr,
          ...(input.reason.trim() ? { reason: input.reason.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setChange(null);
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
    },
    // The server's own sentence, never a generic apology. A refused plan
    // change has a rule behind it and the rule is the useful part.
    onError: (error: unknown) =>
      setRefusal(error instanceof Error ? error.message : t('error.unexpected')),
  });

  if (record.isPending) return <SubscriptionLoading />;
  if (record.isError) return <SubscriptionError error={record.error} retry={() => void record.refetch()} />;

  const { subscription, events, arr } = record.data;
  const money = (value: string): string => formatMoney(value, format, { currency: subscription.currency });

  // Which entries a correction has superseded. Shown struck through rather
  // than removed: a ledger that hid its own corrections could not be audited.
  const superseded = new Set(events.filter((e) => e.corrects !== null).map((e) => e.corrects));

  return (
    <div data-component="SubscriptionRecord" className="flex w-full flex-col gap-4">
      <RecordHeader
        kicker={t('object.subscription')}
        title={subscription.number}
        subtitle={subscription.accountName ?? ''}
        fields={[
          { key: 'plan', label: t('revenue.plan'), value: subscription.planLabel ?? '', editable: false },
          {
            key: 'status',
            label: t('revenue.statusLabel'),
            value: t(`revenue.status.${subscription.status}` as 'revenue.status.active'),
            editable: false,
          },
          {
            key: 'startedOn',
            label: t('revenue.startedOn'),
            value: formatDate(`${subscription.startedOn}T12:00:00Z`, format),
            editable: false,
          },
          {
            key: 'quantity',
            label: t('revenue.quantity'),
            value: subscription.quantity ?? '',
            editable: false,
          },
        ]}
        /*
         * Every action opens a dialog that asks for an effective date. There
         * is deliberately no inline edit on any field above: the fields are a
         * fold, and something that looks editable but writes an event is worse
         * than something that plainly is not editable.
         */
        actions={CHANGE_KINDS.map((kind) => ({
          key: kind,
          label: t(`revenue.kind.${kind}` as 'revenue.kind.upgraded'),
          icon: ICON_FOR[kind],
          onClick: () =>
            setChange({
              kind,
              effectiveOn: subscription.startedOn,
              mrr: NEEDS_REASON.includes(kind) ? '0' : subscription.mrr,
              reason: '',
            }),
        }))}
        aside={
          <span className="flex items-start gap-6">
            <MetricTile
              data-metric="mrr"
              label={t('revenue.mrr')}
              value={money(subscription.mrr)}
              labels={TREND_LABELS(t)}
            />
            <MetricTile data-metric="arr" label={t('revenue.arr')} value={money(arr)} labels={TREND_LABELS(t)} />
          </span>
        }
        labels={{
          edit: t('action.edit'),
          save: t('action.save'),
          cancel: t('action.cancel'),
          more: t('list.moreActions'),
        }}
      />

      {refusal ? (
        <p role="alert" data-change-refused className="rounded-card bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {refusal}
        </p>
      ) : null}

      {/* When it ends and what happens then (F-051). Above the event history,
          because the countdown is the thing somebody opened this page for. */}
      <TermPanel subscriptionId={subscriptionId} />

      <Panel>
        <SectionLabel>{t('revenue.history')}</SectionLabel>
        {/* Said in the interface, not only in a comment: the reason nothing
            here has an edit control. */}
        <p className="pb-2 text-meta text-ink-meta">{t('revenue.historyNote')}</p>

        <ol data-plan-history className="flex flex-col">
          {events.map((event) => (
            <li
              key={event.eventId}
              data-plan-event={String(event.eventId)}
              data-superseded={superseded.has(event.eventId) ? 'true' : 'false'}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-t border-t-rule py-2 first:border-t-0"
            >
              <Icon name={ICON_FOR[event.kind]} size="inline" decorative />
              <span className="text-body text-ink">
                {t(`revenue.kind.${event.kind}` as 'revenue.kind.upgraded')}
              </span>

              {/* Two dates, always. Which month a change lands in is decided
                  by the first; the second is only who typed it and when. */}
              <span className="text-meta text-ink-meta">
                {t('revenue.effectiveOn')}{' '}
                <Num data-effective-on={String(event.eventId)} className="text-meta text-ink">
                  {formatDate(`${event.effectiveOn}T12:00:00Z`, format)}
                </Num>
              </span>
              <span className="text-meta text-ink-300">
                {t('revenue.recordedAt')} {formatDateTime(event.recordedAt, format)}
              </span>

              <Num
                data-event-mrr={String(event.eventId)}
                className={
                  superseded.has(event.eventId)
                    ? 'ms-auto text-body text-ink-300 line-through'
                    : 'ms-auto text-body text-ink'
                }
              >
                {money(event.mrr)}
              </Num>

              {event.reason ? (
                <span className="basis-full text-meta text-ink-meta">
                  {t('revenue.reason')}: {event.reason}
                </span>
              ) : null}
              {event.corrects === null ? null : (
                <span className="basis-full text-meta text-warning-text">
                  {t('revenue.corrects', { values: { id: String(event.corrects) } })}
                </span>
              )}
              {superseded.has(event.eventId) ? (
                <span className="basis-full text-meta text-warning-text">{t('revenue.superseded')}</span>
              ) : null}
            </li>
          ))}
        </ol>
      </Panel>

      <ConfirmDialog
        open={change !== null}
        data-change-dialog
        title={change ? t(`revenue.kind.${change.kind}` as 'revenue.kind.upgraded') : t('revenue.recordChange')}
        confirmLabel={t('revenue.save')}
        confirmIntent={change && NEEDS_REASON.includes(change.kind) ? 'danger' : 'primary'}
        busy={recordChange.isPending}
        /* Blocked with a sentence rather than a disabled button in silence:
           the server refuses an unexplained ending, and a person should find
           that out before they press the button, not after. */
        blockedReason={
          change && NEEDS_REASON.includes(change.kind) && !change.reason.trim()
            ? t('revenue.reasonRequired')
            : undefined
        }
        labels={{ cancel: t('revenue.cancel') }}
        onCancel={() => {
          setChange(null);
          setRefusal(null);
        }}
        onConfirm={() => {
          if (change) recordChange.mutate(change);
        }}
      >
        {change ? (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-0.5 text-meta text-ink-label">
              {t('revenue.effectiveOn')}
              <input
                type="date"
                data-effective-input
                aria-label={t('revenue.effectiveOn')}
                value={change.effectiveOn}
                onChange={(event) =>
                  setChange((current) => (current ? { ...current, effectiveOn: event.target.value } : current))
                }
                className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
              />
              {/* The sentence that makes the whole model visible at the point
                  it matters. A change agreed today usually is not effective
                  today, and the default here is not today for that reason. */}
              <span className="text-meta text-ink-meta">{t('revenue.effectiveHint')}</span>
            </label>

            {NEEDS_REASON.includes(change.kind) ? null : (
              <label className="flex flex-col gap-0.5 text-meta text-ink-label">
                {t('revenue.newMrr')}
                <input
                  type="text"
                  inputMode="decimal"
                  data-mrr-input
                  aria-label={t('revenue.newMrr')}
                  value={change.mrr}
                  onChange={(event) =>
                    setChange((current) => (current ? { ...current, mrr: event.target.value } : current))
                  }
                  className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
                />
              </label>
            )}

            <label className="flex flex-col gap-0.5 text-meta text-ink-label">
              {t('revenue.reason')}
              <textarea
                data-reason-input
                aria-label={t('revenue.reason')}
                value={change.reason}
                onChange={(event) =>
                  setChange((current) => (current ? { ...current, reason: event.target.value } : current))
                }
                className="rounded-control border border-rule bg-surface px-2 py-1 text-dense text-ink"
              />
            </label>
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

const ICON_FOR: Record<EventKind, IconName> = {
  started: 'Play',
  upgraded: 'TrendUp',
  downgraded: 'TrendDown',
  'quantity-changed': 'Hash',
  'price-changed': 'Tag',
  paused: 'Pause',
  resumed: 'Play',
  cancelled: 'XCircle',
  reactivated: 'ArrowCounterClockwise',
  corrected: 'PencilSimple',
};

const TREND_LABELS = (t: (key: 'revenue.increase' | 'revenue.decrease' | 'revenue.nothingMoved') => string) => ({
  up: (chg: string) => `${t('revenue.increase')} ${chg}`,
  down: (chg: string) => `${t('revenue.decrease')} ${chg}`,
  flat: t('revenue.nothingMoved'),
});

function SubscriptionEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="ArrowsClockwise"
      title={t('revenue.subscription.empty.title')}
      body={t('revenue.subscription.empty.body')}
      primary={{ label: t('empty.action.openDeals'), icon: 'Target', href: '/deals' }}
    />
  );
}

function SubscriptionLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {/* Header first, history after: the header is what tells a person they
          opened the right subscription. */}
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function SubscriptionRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This subscription could not be loaded"
      body={error instanceof Error ? error.message : 'The plan history did not load.'}
      onRetry={retry}
    />
  );
}

const SubscriptionError = SubscriptionRecordError;

export const screen = defineScreen({
  id: 'SC-44',
  route: '/subscriptions/:id',
  surface: 'page',
  title: 'Subscription',
  // F-051 as well: the term timeline and the renewal countdown this screen's
  // own specification has always named, and which had nothing behind them.
  features: ['F-164', 'F-051'],
  entitlement: 'F-164',
  reachedBy: [
    { kind: 'screen', from: 'SC-45', how: 'a row in the movement table' },
  ],
  components: ['RecordHeader', 'MetricTile'],
  elements: ['SubscriptionRecord'],
  /*
   * The dialog is not on the page until somebody asks for a change, and that
   * is the point of it: an effective date has to be asked for before anything
   * is written. Declaring it here rather than in `components` means a UI test
   * has to drive the screen until it appears, which is the stronger of the two
   * demands.
   */
  afterInteraction: ['ConfirmDialog'],
  sampleRecord: { objectKey: 'subscription', param: 'id' },
  states: {
    empty: SubscriptionEmpty,
    loading: SubscriptionLoading,
    error: SubscriptionRecordError,
  },
  component: SubscriptionRecordScreen,
});

export default screen;
